import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';

export interface ExploreFilterState {
  sectionIds: string[];
  categories: string[];
  types: string[];
  priceFilter: string[];
}

interface SectionInfo {
  id: string;
  name: string;
  color: string;
  itemCount: number;
}

interface ExploreFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: ExploreFilterState;
  onFiltersChange: (filters: ExploreFilterState) => void;
  items: any[];
  config: any;
  translations: Record<string, Record<string, string>>;
  sections: SectionInfo[];
}

export const ExploreFilterDrawer: React.FC<ExploreFilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  items,
  config,
  translations,
  sections,
}) => {
  const { i18n } = useTranslation();

  const filteredBySection = useMemo(() => {
    if (filters.sectionIds.length === 0) return items;
    return items.filter((i: any) => filters.sectionIds.includes(i._sectionId));
  }, [items, filters.sectionIds]);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(filteredBySection.map((i: any) => i.category))).filter(Boolean);
    return unique.sort((a, b) => {
      const at = translations[a]?.[i18n.language] || translations[a]?.fr || a;
      const bt = translations[b]?.[i18n.language] || translations[b]?.fr || b;
      return at.localeCompare(bt);
    });
  }, [filteredBySection, translations, i18n.language]);

  const types = useMemo(() => {
    if (filters.categories.length === 0) return [];
    const filtered = filteredBySection.filter((i: any) => filters.categories.includes(i.category));
    const unique = Array.from(new Set(filtered.map((i: any) => i.type))).filter(Boolean);
    return unique.sort((a, b) => {
      const at = translations[a]?.[i18n.language] || translations[a]?.fr || a;
      const bt = translations[b]?.[i18n.language] || translations[b]?.fr || b;
      return at.localeCompare(bt);
    });
  }, [filteredBySection, filters.categories, translations, i18n.language]);

  const handleSectionToggle = (sectionId: string) => {
    if (!sectionId) {
      onFiltersChange({ ...filters, sectionIds: [], categories: [], types: [], priceFilter: [] });
      return;
    }
    const next = filters.sectionIds.includes(sectionId)
      ? filters.sectionIds.filter((s) => s !== sectionId)
      : [...filters.sectionIds, sectionId];
    onFiltersChange({ ...filters, sectionIds: next, categories: [], types: [], priceFilter: [] });
  };

  const handleCategoryToggle = (category: string) => {
    if (!category) {
      onFiltersChange({ ...filters, categories: [], types: [] });
      return;
    }
    const next = filters.categories.includes(category)
      ? filters.categories.filter((c) => c !== category)
      : [...filters.categories, category];
    onFiltersChange({ ...filters, categories: next, types: [] });
  };

  const handleTypeToggle = (type: string) => {
    const next = filters.types.includes(type)
      ? filters.types.filter((t) => t !== type)
      : [...filters.types, type];
    onFiltersChange({ ...filters, types: next });
  };

  const handleClearTypes = () => {
    onFiltersChange({ ...filters, types: [] });
  };

  const handleReset = () => {
    onFiltersChange({ sectionIds: [], categories: [], types: [], priceFilter: [] });
  };

  const activeCount =
    filters.sectionIds.length +
    filters.categories.length +
    filters.types.length;

  const chipBase = 'px-2.5 py-1 text-xs font-medium transition-all border rounded';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-[200]"
            onClick={onClose}
          />

          <motion.div
            key="drawer"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed left-0 right-0 z-[400] bg-white rounded-t-xl shadow-2xl"
            style={{ bottom: '40px', maxHeight: 'calc(80vh - 40px)' }}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  {getTranslatedText(translations, 'filter_drawer_title', i18n.language) || 'Filtres'}
                </h2>
                {activeCount > 0 && (
                  <span
                    className="text-xs font-bold px-1.5 py-0.5 rounded text-white"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    {activeCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-xs font-medium"
                    style={{ color: config['major-color'] }}
                  >
                    {getTranslatedText(translations, 'filter_reset', i18n.language) || 'Réinitialiser'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div
              className="px-4 pb-6 overflow-y-auto"
              style={{ maxHeight: 'calc(80vh - 100px)' }}
            >
              <div className="w-8 h-0.5 bg-gray-200 rounded-full mx-auto mt-3 mb-4" />

              <div className="space-y-4">
                {sections.length > 1 && (
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                      Section
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleSectionToggle('')}
                        className={chipBase}
                        style={
                          filters.sectionIds.length === 0
                            ? { backgroundColor: config['major-color'], color: '#fff', borderColor: config['major-color'] }
                            : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                        }
                      >
                        {getTranslatedText(translations, 'filter_all_categories', i18n.language) || 'Toutes'}
                      </button>
                      {sections.map((sec) => {
                        const isActive = filters.sectionIds.includes(sec.id);
                        return (
                          <button
                            key={sec.id}
                            type="button"
                            onClick={() => handleSectionToggle(sec.id)}
                            className={chipBase}
                            style={
                              isActive
                                ? { backgroundColor: sec.color, color: '#fff', borderColor: sec.color }
                                : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                            }
                          >
                            {sec.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                    {getTranslatedText(translations, 'filter_category_label', i18n.language) || 'Catégorie'}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleCategoryToggle('')}
                      className={chipBase}
                      style={
                        filters.categories.length === 0
                          ? { backgroundColor: config['major-color'], color: '#fff', borderColor: config['major-color'] }
                          : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                      }
                    >
                      {getTranslatedText(translations, 'filter_all_categories', i18n.language) || 'Toutes'}
                    </button>
                    {categories.map((cat) => {
                      const isActive = filters.categories.includes(cat);
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => handleCategoryToggle(cat)}
                          className={chipBase}
                          style={
                            isActive
                              ? { backgroundColor: config['dark-color'], color: '#fff', borderColor: config['dark-color'] }
                              : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                          }
                        >
                          {getTranslatedCategory(translations, cat, i18n.language)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {filters.categories.length > 0 && types.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {getTranslatedText(translations, 'filter_type_label', i18n.language) || 'Type'}
                      </label>
                      {filters.types.length > 0 && (
                        <button
                          type="button"
                          onClick={handleClearTypes}
                          className="text-[10px] font-medium"
                          style={{ color: config['major-color'] }}
                        >
                          {getTranslatedText(translations, 'filter_reset', i18n.language) || 'Réinitialiser'}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {types.map((t) => {
                        const isActive = filters.types.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => handleTypeToggle(t)}
                            className={chipBase}
                            style={
                              isActive
                                ? { backgroundColor: config['medium-color'], color: '#fff', borderColor: config['medium-color'] }
                                : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                            }
                          >
                            {getTranslatedCategory(translations, t, i18n.language)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: config['major-color'] }}
                >
                  {getTranslatedText(translations, 'filter_apply', i18n.language) || 'Appliquer'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
